import React, { useEffect } from 'react';
import { withTranslation } from 'config/i18n';
import { makeStyles, Box } from '@material-ui/core';
import WelcomeBanner from 'components/CommercialHost/WelcomeBanner';
import HowToStart from 'components/CommercialHost/HowToStart';
import CommercialQuestionsSection from 'components/CommercialHost/CommercialQuestionsSection';
import ReadyToEarn from 'components/CommercialHost/ReadyToEarn';
import IncreaseProfit from 'components/CommercialHost/IncreaseProfit';
import { serverFetch } from 'utils/serverFetch';
import Head from 'next/head';
import { API } from 'config/axios';
import generateHreflang from 'utils/generateHreflang';
import { hotjar } from 'react-hotjar';
import getConfig from 'next/config';
import isCrawlers from 'common/utils/isCrawlers';
import LazyLoad from 'common/components/LazyLoad';
import generateMetaData from 'utils/generateMetaData';

const useStyles = makeStyles((theme) => ({
  pageContainer: {
    backgroundColor: theme.palette.common.white,
    [theme.breakpoints.only('md')]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
  },
  bannerContainer: {
    backgroundColor: theme.palette.common.white,
  },
}));

const CommercialHost = ({
  t, seoValues, headLinks, ssrRender,
}) => {
  const classes = useStyles();
  const {
    publicRuntimeConfig: { envVars },
  } = getConfig();
  const hotjarEnabledCheck = envVars.HOTJAR_ENABLED === 'true';

  useEffect(() => {
    if (hotjarEnabledCheck && !ssrRender) {
      hotjar.initialize(envVars.HOTJAR_SITE_ID, 6);
    }
  }, []);
  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Eagle Share',
    url: 'https://www.eagleshare.com/',
    logo: 'https://www.eagleshare.com/imgs/es/og_es_logo.jpg',
    description: seoValues?.description,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '11860 S. La Cienega Blvd. Hawthorne, California, 90250',
      addressLocality: 'Los Angeles',
      addressRegion: 'California',
      addressCountry: 'United States',
    },
    sameAs: ['https://www.instagram.com/eagleshare/'],

  };
  return (
    <>
      <Head>
        {seoValues && generateMetaData(seoValues)}
        {!process.browser && (
          <>
            <link rel="canonical" href={headLinks.canonical} />
            {headLinks.urls.map((local) => (
              <link
                key={local.hreflang}
                rel="alternate"
                href={local.url}
                hrefLang={local.hreflang}
              />
            ))}
          </>
        )}
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />
      </Head>
      <Box className={classes.bannerContainer}>
        <WelcomeBanner i18n={t} />
      </Box>
      <Box className={classes.pageContainer}>
        <LazyLoad ssrRender={ssrRender}>
          <IncreaseProfit i18n={t} />
        </LazyLoad>
        <LazyLoad ssrRender={ssrRender}>
          <HowToStart i18n={t} />
        </LazyLoad>
        <LazyLoad ssrRender={ssrRender}>
          <CommercialQuestionsSection i18n={t} />
        </LazyLoad>
      </Box>
      <LazyLoad ssrRender={ssrRender}>
        <ReadyToEarn i18n={t} />
      </LazyLoad>
    </>
  );
};

CommercialHost.getInitialProps = async (ctx) => {
  let seoValues;
  const params = { preferred_seo_identifer: '/commercial-host' };
  const ssrRender = ctx.req ? isCrawlers(ctx.req.headers['user-agent']) : false;
  if (ssrRender) {
    const { success, data } = await serverFetch(ctx, API.seoValues, params);
    if (success && data) {
      seoValues = data;
    }
  }

  // generate canonical and hrefLang
  const host = ctx.req ? ctx.req.headers.host : window.location.host;
  let url;
  ctx.req ? (url = ctx.req.url) : (url = ctx.asPath);
  const headLinks = generateHreflang(url, '/commercial-host', host);

  return ({
    namespacesRequired: [
      'fe_commercial_host_banner_shared',
      'fe_how_to_start_component',
      'fe_commercial_questions_component',
      'fe_increase_profit_component',
    ],
    seoValues,
    headLinks,
    pageIdentifier: 'commercial-host',
    pendingOnLayout: false,
    ssrRender,
  });
};
export default withTranslation([
  'fe_commercial_host_banner_shared',
  'fe_how_to_start_component',
  'fe_commercial_questions_component',
  'fe_increase_profit_component',
])(CommercialHost);
